<template>
  <div class="animated fadeIn page-shell">
    <div class="page-main">
      <header-padrao class="mb-2" icon="fa fa-industry" titulo="PROCESSO DE PRODUÇÃO POR FÓRMULA">
        <template #subtitulo>
          <div class="d-flex align-items-center flex-wrap">
            <span class="mr-2">
              <i class="fa fa-key mr-1 text-info"></i>
              ID: <strong class="text-white">{{ registro.ID || 'Novo' }}</strong>
            </span>
            <span v-if="registro.SITUACAO" class="mr-2">
              <b-badge :variant="getVariantStatus(registro.SITUACAO)" pill>
                {{ getLabelStatus(registro.SITUACAO) }}
              </b-badge>
            </span>
            <span v-if="registro.LOTE" class="text-white-50">Lote final {{ registro.LOTE }}</span>
          </div>
        </template>

        <template #acoes>
          <b-button v-if="itensExibicao.length > 0" variant="link" class="btn-glass btn-glass-secondary px-4 font-weight-bold mr-2" @click.stop.prevent="imprimirEspelhoProcesso">
            <i class="fa fa-file-text-o mr-1"></i> ESPELHO
          </b-button>
          <b-button v-if="podeEditarCabecalho" variant="link" class="btn-glass btn-glass-success px-4 font-weight-bold" :disabled="isBusy" @click.stop.prevent="gravar">
            <i class="fa fa-save mr-1"></i> {{ registro.ID ? 'SALVAR' : 'GRAVAR' }}
          </b-button>
          <b-button v-if="registro.ID && !podeReabrirProcesso" variant="warning" pill class="ml-2 font-weight-bold shadow-sm px-4" :disabled="isBusy || !podeFinalizarProcesso" @click.stop.prevent="finalizarProcesso">
            <i class="fa fa-lock mr-1"></i> Finalizar processo
          </b-button>
          <b-button v-if="podeReabrirProcesso" variant="info" pill class="ml-2 font-weight-bold shadow-sm px-4" :disabled="isBusy" @click.stop.prevent="reabrirProcesso">
            <i class="fa fa-unlock mr-1"></i> Reabrir processo
          </b-button>
          <b-button variant="light" pill class="ml-2 font-weight-bold shadow-sm px-4" @click.stop.prevent="voltar">
            <i class="fa fa-arrow-left mr-1"></i> Voltar
          </b-button>
        </template>
      </header-padrao>

      <b-row class="px-2">
        <b-col sm="12">
          <b-card class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
            <template #header>
              <div class="d-flex align-items-center">
                <div class="bg-warning text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center section-icon">
                  <i class="fa fa-cogs"></i>
                </div>
                <div>
                  <h6 class="mb-0 font-weight-bold">Cabeçalho do Processo</h6>
                  <small class="text-muted">Selecione a fórmula, revise o produto final e defina o lote e os centros macro do processo.</small>
                </div>
              </div>
            </template>

            <b-alert v-if="!podeEditarCabecalho" show variant="secondary" class="mb-3">
              Este processo está {{ getLabelStatus(registro.SITUACAO).toLowerCase() }} e está disponível somente para consulta.
            </b-alert>

            <b-alert v-else-if="possuiApontamentosAtivos" show variant="warning" class="mb-3">
              Já existe apontamento registrado para este processo. Fórmula, produto final, lote final, centros e itens previstos ficam bloqueados para preservar a integridade operacional.
            </b-alert>

            <b-row>
              <b-col md="6">
                <label class="modal-label-premium">Fórmula *</label>
                <ProducaoFormulaProcessoFormulaDropDown
                  v-model="registro.PRD_FORMULA_ID"
                  :idInicial="registro.PRD_FORMULA_ID"
                  :state="stateFormula"
                  :classeRequired="!registro.PRD_FORMULA_ID"
                  :desabilitarInput="!podeEditarEstrutura"
                  @change="onFormulaChange">
                </ProducaoFormulaProcessoFormulaDropDown>
                <small class="text-muted d-block mt-1">A fórmula define automaticamente o produto final, os padrões de produção e sugere o centro inicial de cada matéria-prima.</small>
              </b-col>
              <b-col md="4">
                <label class="modal-label-premium">Produto Final</label>
                <b-form-input :value="registro.PRODUTO_CODIGONOME || ''" disabled class="rounded-pill shadow-xs bg-light campo-desabilitado"></b-form-input>
                <small class="text-muted d-block mt-1">Definido automaticamente pela fórmula selecionada.</small>
              </b-col>
              <b-col md="2">
                <label class="modal-label-premium">Situação</label>
                <b-form-input :value="getLabelStatus(registro.SITUACAO)" disabled class="rounded-pill shadow-xs bg-light campo-desabilitado"></b-form-input>
                <small class="text-muted d-block mt-1">Controlada automaticamente pelo processo.</small>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col md="3">
                <label class="modal-label-premium">Data do Processo *</label>
                <b-form-input v-model="registro.DATA_PROCESSO" type="date" class="rounded-pill shadow-xs" :disabled="!podeEditarCabecalho" :state="stateDataProcesso"></b-form-input>
                <small class="text-muted d-block mt-1">Data operacional do processo.</small>
              </b-col>
              <b-col md="3">
                <label class="modal-label-premium">Lote Final *</label>
                <b-form-input v-model="registro.LOTE" :disabled="!podeEditarEstrutura" maxlength="30" class="rounded-pill shadow-xs" :state="stateLote"></b-form-input>
                <small class="text-muted d-block mt-1">Identificador único do lote final.</small>
              </b-col>
              <b-col md="3">
                <label class="modal-label-premium">Multiplicador da Fórmula (BATIDAS) *</label>
                <vue-autonumeric v-model="registro.MULTIPLICADOR" :options="formatoMultiplicador" :class="['form-control', 'rounded-pill', 'text-right', 'shadow-xs', {'is-invalid': stateMultiplicador === false}]" :disabled="!podeEditarEstrutura"></vue-autonumeric>
                <small class="text-muted d-block mt-1">Vezes/Batidas (calcula produção e insumos).</small>
              </b-col>
              <b-col md="3">
                <label class="modal-label-premium">Insumos na Fórmula</label>
                <b-form-input :value="itensExibicao.length + ' matérias-primas'" disabled class="rounded-pill shadow-xs bg-light campo-desabilitado text-center font-weight-bold"></b-form-input>
                <small class="text-muted d-block mt-1">Total de itens gerados pela fórmula.</small>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col md="3">
                <label class="modal-label-premium">Produção Prevista (Peças/Volumes)</label>
                <div class="d-flex align-items-center">
                  <vue-autonumeric v-model="registro.PECAS_PREVISTA" :options="formatoPecas" class="form-control rounded-pill text-right shadow-xs bg-light" :disabled="true"></vue-autonumeric>
                  <b-badge v-if="registro.ID_UNIDADE_COMERCIAL" variant="light" pill class="ml-2 px-3 py-2 border text-primary font-weight-bold shadow-sm" style="font-size: 0.9rem;">
                    {{ registro.ID_UNIDADE_COMERCIAL }}
                  </b-badge>
                </div>
                <small class="text-muted d-block mt-1">Calculado pelo multiplicador.</small>
              </b-col>
              <b-col md="3">
                <label class="modal-label-premium">Produção Prevista (Quantidade)</label>
                <vue-autonumeric v-model="registro.QUANT_PREVISTA" :options="formatoQuantidade" class="form-control rounded-pill text-right shadow-xs bg-light" :disabled="true"></vue-autonumeric>
                <small class="text-muted d-block mt-1">Calculado pelo multiplicador.</small>
              </b-col>
              <b-col md="3">
                <label class="modal-label-premium">Centro de Produção *</label>
                <CentroEstoqueDropDown v-model="registro.CENTRO_PRODUCAO_ID" :disabled="!podeEditarEstrutura" :state="registro.CENTRO_PRODUCAO_ID ? null : false" :input-class="['rounded-pill', 'shadow-xs']"></CentroEstoqueDropDown>
                <small class="text-muted d-block mt-1">Lançamento do produto final.</small>
              </b-col>
              <b-col md="3">
                <label class="modal-label-premium">Template de Custos Op.</label>
                <CustoOperacionalDropDown v-model="registro.CENTRO_CUSTO_OP_ID" :disabled="!podeEditarCabecalho" :input-class="['rounded-pill', 'shadow-xs']"></CustoOperacionalDropDown>
                <small class="text-muted d-block mt-1">Custos não-estruturais.</small>
              </b-col>
            </b-row>

            <b-row class="mt-3">
              <b-col md="12">
                <label class="modal-label-premium">Observação</label>
                <b-form-textarea v-model="registro.OBSERVACAO" rows="3" maxlength="200" class="shadow-xs" :disabled="!podeEditarCabecalho"></b-form-textarea>
              </b-col>
            </b-row>
          </b-card>

          <b-card class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
            <template #header>
              <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                <div class="d-flex align-items-center">
                  <div class="bg-primary text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center section-icon">
                    <i class="fa fa-list-alt"></i>
                  </div>
                  <div>
                    <h6 class="mb-0 font-weight-bold">Itens Previstos</h6>
                    <small class="text-muted">Os itens são gerados automaticamente a partir da fórmula e a definição do consumo previsto continua sendo feita por centro de estoque.</small>
                  </div>
                </div>
                <div>
                  <b-badge variant="info" pill class="mr-2 px-3 py-2">Custo Total Previsto: {{ formatarMoeda(totalCustoPrevisto) }}</b-badge>
                  <b-button variant="outline-primary" pill class="px-4 font-weight-bold" :disabled="!podeEditarEstrutura || !registro.PRD_FORMULA_ID" @click="regerarPreviewItens">
                    <i class="fa fa-refresh mr-1"></i> Regerar previsão
                  </b-button>
                </div>
              </div>
            </template>

            <b-alert v-if="!registro.PRD_FORMULA_ID" show variant="warning" class="mb-3">
              Selecione a fórmula para carregar o produto final e gerar os itens previstos.
            </b-alert>

            <b-alert v-else-if="!isProcessoFinalizado" show variant="light" class="mb-3 border">
              As reservas deste processo são lógicas e consideram os próprios itens previstos em lançamento. A análise de saldo do processo continua sendo feita por centro de estoque.
            </b-alert>

            <b-alert v-if="!isProcessoFinalizado && itensExibicao.length > 0 && custosPrevistosZerados" show variant="warning" class="mb-3">
              Existem itens previstos sem custo de referência definido.
            </b-alert>

            <b-table
              v-if="registro.PRD_FORMULA_ID"
              :items="itensExibicao"
              :fields="colunasItens"
              hover
              small
              responsive
              show-empty
              empty-text="Nenhum item previsto para exibição"
              class="mb-0 table-finance">
              <template #cell(PRODUTO_CODIGONOME)="row">
                <div>
                  <div>{{ row.item.PRODUTO_CODIGONOME }}</div>
                </div>
              </template>

              <template #cell(CENTRO_ESCOLHIDO)="row">
                <div>
                  <div v-if="itemPossuiCentroSelecionado(row.item)" :class="isProcessoFinalizado ? 'font-weight-bold text-dark' : 'font-weight-bold text-primary'">
                    {{ row.item.CENTRO_ESTOQUE_CODIGONOME }}
                    <b-badge v-if="!isProcessoFinalizado" variant="primary" pill class="ml-1">Selecionado</b-badge>
                  </div>
                  <div v-else class="font-weight-bold text-muted">
                    {{ isProcessoFinalizado ? 'Centro não informado' : 'Sem centro selecionado' }}
                    <b-badge v-if="!isProcessoFinalizado" variant="light" pill class="ml-1 border text-muted">Pendência</b-badge>
                  </div>
                  <small v-if="!isProcessoFinalizado && row.item.CENTRO_ESTOQUE_CODIGONOME_SUGERIDO" class="text-muted d-block mt-1">
                    <span class="font-weight-bold">Sugestão:</span> {{ row.item.CENTRO_ESTOQUE_CODIGONOME_SUGERIDO }}
                  </small>
                  <small v-else-if="!isProcessoFinalizado" class="text-muted d-block mt-1">Sem sugestão inicial da fórmula.</small>
                  <small v-if="!isProcessoFinalizado && itemCentroSelecionadoInsuficiente(row.item, row.index)" class="text-danger d-block mt-1">
                    <i class="fa fa-exclamation-circle mr-1"></i>O centro selecionado está com saldo insuficiente para este consumo previsto.
                  </small>
                </div>
              </template>

              <template #cell(QUANT_PREVISTA)="row">
                <div class="text-right">
                  <div class="consumo-resultado__valor">{{ formatarNumero(row.item.QUANT_PREVISTA) }}</div>
                  <small class="consumo-resultado__legenda d-block">Consumo planejado</small>
                </div>
              </template>



              <template #cell(SALDO_DISPONIVEL)="row">
                <div v-if="obterSaldoLinha(row.item, row.index)" class="text-right">
                  <div :class="['saldo-resultado__valor', { 'saldo-resultado__valor--critico': !itemTemSaldoSuficiente(row.item, row.index) }]">
                    {{ formatarNumero(obterSaldoLinha(row.item, row.index).quantidade) }}
                  </div>
                  <small class="text-muted d-block">Saldo disponível</small>
                  <b-badge v-if="!isProcessoFinalizado" :variant="itemTemSaldoSuficiente(row.item, row.index) ? 'light' : 'warning'" pill class="mt-1 border saldo-resultado__badge">
                    {{ itemTemSaldoSuficiente(row.item, row.index) ? 'Atende ao previsto' : 'Abaixo do previsto' }}
                  </b-badge>
                </div>
                <div v-else class="text-muted small text-right">
                  {{ isProcessoFinalizado ? '--' : 'Selecione um centro' }}
                </div>
              </template>

              <template #cell(CUSTO_UNIT_PREV)="row">
                <span class="text-right d-block">{{ formatarNumero(row.item.CUSTO_UNIT_PREV) }}</span>
              </template>

              <template #cell(CUSTO_TOT_PREV)="row">
                <span class="text-right d-block font-weight-bold text-info">{{ formatarNumero(row.item.CUSTO_TOT_PREV) }}</span>
              </template>

              <template #cell(ACOES)="row">
                <div class="text-center">
                  <template v-if="!isProcessoFinalizado">
                    <b-button variant="outline-primary" size="sm" pill class="px-3" :disabled="!podeEditarEstrutura" @click="abrirModalCentro(row.item, row.index)">
                      <i class="fa fa-building mr-1"></i>{{ itemPossuiCentroSelecionado(row.item) ? 'Trocar centro' : 'Selecionar centro' }}
                    </b-button>
                    <b-button variant="outline-secondary" size="sm" pill class="px-3 ml-2" :disabled="!podeEditarEstrutura || !itemPossuiCentroSelecionado(row.item)" @click="limparCentroItem(row.index)">
                      <i class="fa fa-eraser mr-1"></i>Limpar seleção
                    </b-button>
                  </template>
                  <span v-else class="text-muted small">Consulta</span>
                </div>
              </template>
            </b-table>

            <div v-if="registro.PRD_FORMULA_ID" class="border-top bg-light px-3 py-3">
              <b-row>
                <b-col md="3">
                  <small class="text-muted d-block">Total de itens</small>
                  <strong>{{ totalItensPrevistos }}</strong>
                </b-col>
                <b-col md="3">
                  <small class="text-muted d-block">Soma dos consumos previstos</small>
                  <strong>{{ formatarNumero(totalConsumoPrevisto) }}</strong>
                </b-col>
                <b-col md="3">
                  <small class="text-muted d-block">Soma dos consumos previstos</small>
                  <strong>{{ formatarNumero(totalConsumoPrevisto) }}</strong>
                </b-col>
                <b-col md="3">
                  <small class="text-muted d-block">Custo total previsto</small>
                  <strong class="text-info">{{ formatarMoeda(totalCustoPrevisto) }}</strong>
                </b-col>
              </b-row>
            </div>
          </b-card>

          <!-- CUSTOS PREVISTOS -->
          <b-card v-if="registro.CENTRO_CUSTO_OP_ID" class="shadow-sm mb-3" :class="{'bg-dark border-secondary': isDark}">
            <template #header>
              <div class="d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2">
                <div class="d-flex align-items-center">
                  <div class="bg-success text-white rounded-circle p-2 mr-3 d-flex align-items-center justify-content-center section-icon">
                    <i class="fa fa-dollar"></i>
                  </div>
                  <div>
                    <h6 class="mb-0 font-weight-bold">Custos Operacionais Previstos</h6>
                    <small class="text-muted">Despesas adicionais estimadas e vinculadas por meio do Template de Custo Operacional.</small>
                  </div>
                </div>
              </div>
            </template>
            
            <b-table
              :items="itensCustoPrevisto"
              :fields="[
                { key: 'TIPO_ITEM', label: 'Tipo', class: 'align-middle text-center' },
                { key: 'NATUREZA_DESCRICAO', label: 'Natureza', class: 'align-middle font-weight-bold' },
                { key: 'DESCRICAO_EXIBICAO', label: 'Detalhes / Produto', class: 'align-middle' },
                { key: 'VALOR', label: 'Vlr Fixo', class: 'align-middle text-right' },
                { key: 'QTDE', label: 'Qtde', class: 'align-middle text-right' },
                { key: 'UNITARIO', label: 'Vlr Unit', class: 'align-middle text-right' },
                { key: 'TOTAL', label: 'Total Estimado', class: 'align-middle text-right text-success font-weight-bold' }
              ]"
              hover
              small
              responsive
              show-empty
              empty-text="Nenhum item adicionado ao template selecionado"
              class="mb-0 table-finance"
            >
              <template #cell(TIPO_ITEM)="ctx">
                 <b-badge :variant="ctx.item.TIPO_ITEM === 'P' ? 'info' : 'secondary'">{{ ctx.item.TIPO_ITEM === 'P' ? 'Produto' : 'Manual' }}</b-badge>
              </template>
              <template #cell(DESCRICAO_EXIBICAO)="ctx">
                 <div v-if="ctx.item.TIPO_ITEM === 'P'">
                    <span class="d-block font-weight-bold text-primary">{{ ctx.item.PRODUTO_DESCRICAO || 'Produto sem descrição' }}</span>
                    <small class="text-muted" v-if="ctx.item.DESCRICAO">{{ ctx.item.DESCRICAO }}</small>
                 </div>
                 <div v-else>
                    {{ ctx.item.DESCRICAO || '-' }}
                 </div>
              </template>
              <template #cell(VALOR)="ctx">{{ formatarMoeda(ctx.item.VALOR) }}</template>
              <template #cell(QTDE)="ctx">{{ formatarNumero(ctx.item.QTDE) }}</template>
              <template #cell(UNITARIO)="ctx">{{ formatarMoeda(ctx.item.UNITARIO) }}</template>
              <template #cell(TOTAL)="ctx">{{ formatarMoeda((Number(ctx.item.VALOR)||0) + ((Number(ctx.item.QTDE)||0) * (Number(ctx.item.UNITARIO)||0))) }}</template>
              
              <template #custom-foot="{ fields }">
                <b-tr>
                  <b-td :colspan="fields.length - 1" class="text-right bg-light py-2 px-3 font-weight-bold">Soma dos Custos Operacionais:</b-td>
                  <b-td class="text-right bg-light py-2 px-3 text-success font-weight-bold" style="font-size: 1rem;">
                    {{ formatarMoeda(itensCustoPrevisto.reduce((acc, it) => acc + (Number(it.VALOR)||0) + ((Number(it.QTDE)||0) * (Number(it.UNITARIO)||0)), 0)) }}
                  </b-td>
                </b-tr>
              </template>
            </b-table>
          </b-card>

        </b-col>
      </b-row>

      <b-modal
        ref="modal-centro-item"
        id="modal-centro-item"
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg premium-modal-content"
        centered
        size="xl"
        scrollable>
        <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #125a70 0%, #0c3140 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
            <i class="fa fa-building text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Selecionar Centro do Item Previsto</h5>
            <small class="text-white opacity-75">Saldo atual menos reservas dos processos em lançamento</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$bvModal.hide('modal-centro-item')">
            <i class="fa fa-times fa-lg"></i>
          </b-button>
        </div>

        <div class="modal-info-bar mb-0 bg-light border-bottom shadow-sm p-3">
          <b-row align-v="center">
            <b-col md="5">
              <small class="modal-label-premium">Matéria-prima</small>
              <h6 class="mb-0 font-weight-bold text-dark">{{ modalCentro.item ? modalCentro.item.PRODUTO_CODIGONOME : '-' }}</h6>
            </b-col>
            <b-col md="2" class="border-left">
              <small class="modal-label-premium">Consumo Previsto</small>
              <h6 class="mb-0 font-weight-bold text-primary">{{ formatarNumero(modalCentro.item ? modalCentro.item.QUANT_PREVISTA : 0) }}</h6>
            </b-col>
            <b-col md="3" class="border-left">
              <small class="modal-label-premium">Centro Sugerido</small>
              <h6 class="mb-0 font-weight-bold text-dark">{{ modalCentro.item ? (modalCentro.item.CENTRO_ESTOQUE_CODIGONOME_SUGERIDO || 'Sem sugestão') : '-' }}</h6>
            </b-col>
          </b-row>
        </div>

        <div class="p-4 modal-content-scroll">
          <b-alert v-if="modalCentroSugeridoInsuficiente" show variant="warning" class="mb-3">
            A sugestão inicial deste item não possui saldo líquido suficiente neste momento. Você pode escolher outro centro agora ou manter o item pendente para decidir depois.
          </b-alert>

          <b-overlay :show="modalCentro.carregando" rounded="sm" opacity="0.7" variant="white" spinner-variant="primary">
            <b-table
              :items="modalCentro.centros"
              :fields="colunasModalCentros"
              small
              hover
              responsive
              show-empty
              empty-text="Nenhum centro com saldo localizado para este item"
              :tbody-tr-class="getClasseLinhaModalCentro"
              class="modern-premium-table mb-0">
              <template #cell(CENTRO_ESTOQUE_CODIGONOME)="row">
                <div>
                  <div class="font-weight-bold">{{ row.item.CENTRO_ESTOQUE_CODIGONOME }}</div>
                  <b-badge v-if="row.item.IS_SUGERIDO" variant="info" pill class="mt-1">Sugestão da fórmula</b-badge>
                  <b-badge v-if="row.item.IS_SELECIONADO" variant="primary" pill class="mt-1 ml-1">Selecionado</b-badge>
                </div>
              </template>

              <template #cell(ESTOQUE_QUANT)="row">
                <span class="text-right d-block">{{ formatarNumero(row.item.ESTOQUE_QUANT) }}</span>
              </template>

              <template #cell(QUANT_RESERVADA_AJUSTADA)="row">
                <span class="text-right d-block text-warning">{{ formatarNumero(row.item.QUANT_RESERVADA_AJUSTADA) }}</span>
              </template>

              <template #cell(SALDO_LIQUIDO)="row">
                <div class="text-right">
                  <div :class="row.item.SALDO_SUFICIENTE ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                    {{ formatarNumero(row.item.QUANT_DISPONIVEL_AJUSTADO) }}
                  </div>
                </div>
              </template>

              <template #cell(ACAO)="row">
                <div class="text-center">
                  <b-button :variant="row.item.SALDO_SUFICIENTE ? 'primary' : 'outline-danger'" size="sm" pill class="px-3" @click="selecionarCentroModal(row.item)">
                    <i class="fa fa-check mr-1"></i>{{ row.item.IS_SELECIONADO ? 'Manter seleção' : 'Selecionar' }}
                  </b-button>
                </div>
              </template>
            </b-table>
          </b-overlay>
        </div>
      </b-modal>

      <b-modal
        ref="modal-lote-item"
        id="modal-lote-item"
        hide-header
        hide-footer
        body-class="p-0 overflow-hidden"
        content-class="border-0 shadow-lg premium-modal-content"
        centered
        size="xl"
        scrollable>
        <div class="modal-header-premium p-3 d-flex align-items-center bg-dark" style="background: linear-gradient(135deg, #2f6b41 0%, #193b23 100%) !important;">
          <div class="header-icon-wrapper mr-3 bg-white-10">
            <i class="fa fa-tags text-white"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold text-white">Selecionar Lote do Item Previsto</h5>
            <small class="text-white opacity-75">O centro, o custo e o saldo passam a ser derivados do lote</small>
          </div>
          <b-button variant="link" class="ml-auto text-white p-0 opacity-75" @click="$bvModal.hide('modal-lote-item')">
            <i class="fa fa-times fa-lg"></i>
          </b-button>
        </div>

        <div class="modal-info-bar mb-0 bg-light border-bottom shadow-sm p-3">
          <b-row align-v="center">
            <b-col md="5">
              <small class="modal-label-premium">Matéria-prima</small>
              <h6 class="mb-0 font-weight-bold text-dark">{{ modalLote.item ? modalLote.item.PRODUTO_CODIGONOME : '-' }}</h6>
            </b-col>
            <b-col md="2" class="border-left">
              <small class="modal-label-premium">Consumo Previsto</small>
              <h6 class="mb-0 font-weight-bold text-primary">{{ formatarNumero(modalLote.item ? modalLote.item.QUANT_PREVISTA : 0) }}</h6>
            </b-col>
            <b-col md="3" class="border-left">
              <small class="modal-label-premium">Lote Atual</small>
              <h6 class="mb-0 font-weight-bold text-dark">{{ modalLote.item ? (modalLote.item.LOTE || 'Pendente') : '-' }}</h6>
            </b-col>
          </b-row>
        </div>

        <div class="p-4 modal-content-scroll">
          <b-alert v-if="modalLoteSelecionadoInsuficiente" show variant="warning" class="mb-3">
            O lote atualmente selecionado não atende o consumo previsto. Escolha outro lote ou limpe a seleção para ajustar depois.
          </b-alert>

          <b-overlay :show="modalLote.carregando" rounded="sm" opacity="0.7" variant="white" spinner-variant="success">
            <b-table
              :items="modalLote.lotes"
              :fields="colunasModalLotes"
              small
              hover
              responsive
              show-empty
              empty-text="Nenhum lote disponível com saldo para este produto"
              :tbody-tr-class="getClasseLinhaModalLote"
              class="modern-premium-table mb-0">
              <template #cell(LOTE)="row">
                <div>
                  <div class="font-weight-bold">{{ row.item.LOTE || '--' }}</div>
                  <b-badge v-if="row.item.IS_SELECIONADO" variant="primary" pill class="mt-1">Selecionado</b-badge>
                </div>
              </template>

              <template #cell(LOTE_FORNECEDOR)="row">
                <span>{{ row.item.LOTE_FORNECEDOR || '--' }}</span>
              </template>

              <template #cell(QUANTIDADE)="row">
                <div class="text-right">
                  <div :class="row.item.SALDO_SUFICIENTE ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">{{ formatarNumero(row.item.QUANT_DISPONIVEL_AJUSTADO) }}</div>
                  <small class="text-muted d-block">Saldo bruto {{ formatarNumero(row.item.QUANT_SALDO) }}</small>
                </div>
              </template>

              <template #cell(CUSTO)="row">
                <span class="text-right d-block">{{ formatarNumero(row.item.VL_CUSTO_UNITARIO) }}</span>
              </template>

              <template #cell(VALIDADE)="row">
                <span>{{ formatarData(row.item.DATA_VALIDADE) || '--' }}</span>
              </template>

              <template #cell(OP_ORIGEM)="row">
                <span>{{ row.item.OP_ORIGEM || '--' }}</span>
              </template>

              <template #cell(ACAO)="row">
                <div class="text-center">
                  <b-button :variant="row.item.SALDO_SUFICIENTE ? 'success' : 'outline-danger'" size="sm" pill class="px-3" @click="selecionarLoteModal(row.item)">
                    <i class="fa fa-check mr-1"></i>{{ row.item.IS_SELECIONADO ? 'Manter seleção' : 'Selecionar' }}
                  </b-button>
                </div>
              </template>
            </b-table>
          </b-overlay>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import VueAutonumeric from 'vue-autonumeric'
import * as Tools from '@/tools/tools'
import HeaderPadrao from '@/components/Cec/HeaderPadrao.vue'
import CentroEstoqueDropDown from '@/views/componentes/dropdown/CentroEstoqueDropDown.vue'
import CustoOperacionalDropDown from '@/views/componentes/dropdown/CustoOperacionalDropDown.vue'
import ProducaoFormulaProcessoFormulaDropDown from './ProducaoFormulaProcessoFormulaDropDown.vue'

function escapeHtml(valor) {
  return String(valor == null ? '' : valor)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function formatarDataHoraRelatorio() {
  return new Date().toLocaleString('pt-BR')
}

function novoRegistro() {
  return {
    ID: null,
    DATA_PROCESSO: '',
    PRD_FORMULA_ID: null,
    FORMULA_DESCRICAO: '',
    PRODUTO_ID: null,
    PRODUTO_CODIGONOME: '',
    CENTRO_CONSUMO_ID: null,
    CENTRO_PRODUCAO_ID: null,
    CENTRO_CUSTO_OP_ID: null,
    QUANT_PERDA: 0,
    PECAS_PERDA: 0,
    QUANT_SOBRA: 0,
    PECAS_SOBRA: 0,
    MULTIPLICADOR: 1,
    LOTE: '',
    SITUACAO: 'L',
    SITUACAO_LABEL: 'Lançamento',
    OBSERVACAO: ''
  }
}

function novoModalCentro() {
  return {
    indice: null,
    item: null,
    centros: [],
    carregando: false
  }
}

function novoModalLote() {
  return {
    indice: null,
    item: null,
    lotes: [],
    carregando: false
  }
}

export default {
  components: {
    VueAutonumeric,
    HeaderPadrao,
    CentroEstoqueDropDown,
    CustoOperacionalDropDown,
    ProducaoFormulaProcessoFormulaDropDown
  },
  data() {
    return {
      isBusy: false,
      empresaPreferencial: null,
      registro: novoRegistro(),
      formulaAtual: null,
      itensFormulaBase: [],
      itensPreview: [],
      itensCustoPrevisto: [],
      modalCentro: novoModalCentro(),
      modalLote: novoModalLote(),
      formatoQuantidade: {
        digitGroupSeparator: '.',
        decimalCharacter: ',',
        decimalPlaces: '3',
        roundingMethod: 'U',
        minimumValue: '0'
      },
      formatoPecas: {
        decimalCharacter: ',',
        digitGroupSeparator: '.',
        decimalPlaces: '0',
        roundingMethod: 'U',
        minimumValue: '0'
      },
      formatoMultiplicador: {
        decimalCharacter: ',',
        digitGroupSeparator: '.',
        decimalPlaces: '2',
        roundingMethod: 'U',
        minimumValue: '0.01'
      },
      colunasItens: [
        { key: 'PRODUTO_CODIGONOME', label: 'Matéria-Prima' },
        { key: 'CENTRO_ESCOLHIDO', label: 'Seleção do Consumo', thStyle: { width: '340px' } },
        { key: 'QUANT_PREVISTA', label: 'Consumo Previsto', class: 'text-right', thStyle: { width: '130px' } },
        { key: 'SALDO_DISPONIVEL', label: 'Saldo Líquido', class: 'text-right', thStyle: { width: '130px' } },
        { key: 'CUSTO_UNIT_PREV', label: 'Custo Unit.', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'CUSTO_TOT_PREV', label: 'Custo Total Prev.', class: 'text-right', thStyle: { width: '140px' } },
        { key: 'ACOES', label: 'Ações', class: 'text-center', thStyle: { width: '190px' } }
      ],
      colunasModalCentros: [
        { key: 'CENTRO_ESTOQUE_CODIGONOME', label: 'Centro de Estoque' },
        { key: 'ESTOQUE_QUANT', label: 'Quantidade em Estoque', class: 'text-right', thStyle: { width: '130px' } },
        { key: 'QUANT_RESERVADA_AJUSTADA', label: 'Qtd. Reservada', class: 'text-right', thStyle: { width: '120px' } },
        { key: 'SALDO_LIQUIDO', label: 'Saldo Líquido', class: 'text-right', thStyle: { width: '130px' } },
        { key: 'ACAO', label: 'Selecionar', class: 'text-center', thStyle: { width: '120px' } }
      ],
      colunasModalLotes: []
    }
  },
  computed: {
    isDark() { return this.$store.getters['tema/IS_DARK'] },
    itensStore() { return this.$store.getters['producaoFormulaProcesso/ITENS_PROCESSO'] || [] },
    podeEditar() { return String(this.registro.SITUACAO || 'L') === 'L' },
    isProcessoFinalizado() { return String(this.registro.SITUACAO || 'L') === 'F' },
    podeEditarCabecalho() { return this.podeEditar },
    possuiApontamentosAtivos() { return Number(this.registro.TOTAL_APONTAMENTOS_ATIVOS || 0) > 0 || String(this.registro.BLOQUEIA_ESTRUTURA || '0') === '1' },
    podeEditarEstrutura() { return this.podeEditarCabecalho && !this.possuiApontamentosAtivos },
    podeFinalizarProcesso() { return !!this.registro.ID && this.podeEditarCabecalho && this.possuiApontamentosAtivos },
    podeReabrirProcesso() { return !!this.registro.ID && this.isProcessoFinalizado },
    quantidadeOuPecasInformadas() {
      return Number(this.registro.QUANT_PREVISTA || 0) > 0 || Number(this.registro.PECAS_PREVISTA || 0) > 0
    },
    itensExibicao() {
      return this.itensPreview || []
    },
    totalItensPrevistos() {
      return this.itensExibicao.length
    },
    totalConsumoPrevisto() {
      return this.itensExibicao.reduce((total, item) => total + Number(item.QUANT_PREVISTA || 0), 0)
    },
    totalCustoPrevisto() {
      return this.itensExibicao.reduce((total, item) => total + Number(item.CUSTO_TOT_PREV || 0), 0)
    },
    centrosConsumoSugeridos() {
      const centros = new Map()

      for (const item of (this.itensFormulaBase || [])) {
        const centroId = Number(item.CENTRO_ESTOQUE_ID || item.CENTRO_ESTOQUE_ID_SUGERIDO || 0)
        if (!centroId || centros.has(centroId)) continue

        centros.set(centroId, item.CENTRO_ESTOQUE_CODIGONOME || item.CENTRO_ESTOQUE_CODIGONOME_SUGERIDO || `Centro ${centroId}`)
      }

      return Array.from(centros.entries()).map(([id, label]) => ({ id, label }))
    },
    centroConsumoInformativo() {
      if (this.registro.CENTRO_CONSUMO_CODIGONOME) {
        return this.registro.CENTRO_CONSUMO_CODIGONOME
      }

      if (!this.centrosConsumoSugeridos.length) {
        return 'Sem referência definida'
      }

      if (this.centrosConsumoSugeridos.length === 1) {
        return this.centrosConsumoSugeridos[0].label
      }

      return `${this.centrosConsumoSugeridos.length} centros sugeridos na fórmula`
    },
    custosPrevistosZerados() {
      return this.itensExibicao.length > 0 && Number(this.totalCustoPrevisto || 0) === 0
    },
    stateFormula() {
      if (!this.podeEditarEstrutura) return null
      return this.registro.PRD_FORMULA_ID ? true : false
    },
    stateDataProcesso() {
      if (!this.podeEditarCabecalho) return null
      return this.registro.DATA_PROCESSO && Tools.isDataValida(this.registro.DATA_PROCESSO)
    },
    stateLote() {
      if (!this.podeEditarEstrutura) return null
      return this.registro.LOTE && String(this.registro.LOTE).trim() ? true : false
    },
    stateMultiplicador() {
      if (!this.podeEditarEstrutura) return null
      const mult = Number(this.registro.MULTIPLICADOR || 0)
      return mult > 0 ? true : false
    },
    modalCentroSugeridoInsuficiente() {
      const centroSugerido = this.modalCentro.centros.find(item => item.IS_SUGERIDO)
      return !!(centroSugerido && !centroSugerido.SALDO_SUFICIENTE)
    },
    modalLoteSelecionadoInsuficiente() { return false }
  },
  watch: {
    'registro.MULTIPLICADOR'(val) {
      if (this.podeEditarEstrutura && this.formulaAtual) {
        const mult = Number(val || 0)
        this.registro.PECAS_PREVISTA = mult * Number(this.formulaAtual.PECAS_PADRAO || 0)
        this.registro.QUANT_PREVISTA = mult * Number(this.formulaAtual.QUANT_PADRAO || 0)
      }
      this.atualizarPreviewItens()
    },
    'registro.CENTRO_CUSTO_OP_ID'() {
      this.carregarItensTemplateCusto()
    }
  },
  methods: {
    async carregarItensTemplateCusto() {
      if (!this.registro.CENTRO_CUSTO_OP_ID) {
        this.itensCustoPrevisto = [];
        return;
      }
      try {
        const res = await this.$http.get(`/api/protected/custo-operacional/getById/${this.registro.CENTRO_CUSTO_OP_ID}`)
        if (res.data && res.data.ITENS) {
          this.itensCustoPrevisto = res.data.ITENS;
        } else {
          this.itensCustoPrevisto = [];
        }
      } catch (err) {
        console.error('Erro ao buscar itens do template', err);
        this.itensCustoPrevisto = [];
      }
    },
    getLabelStatus(situacao) {
      if (String(situacao || 'L') === 'C') return 'Cancelado'
      if (String(situacao || 'L') === 'F') return 'Finalizado'
      return 'Lançamento'
    },
    getVariantStatus(situacao) {
      if (String(situacao || 'L') === 'C') return 'danger'
      if (String(situacao || 'L') === 'F') return 'success'
      return 'warning'
    },
    formatarMoeda(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatarNumero(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatarInteiro(valor) {
      return Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },
    formatarData(valor) {
      return Tools.formatarDataBR(valor)
    },
    async obterEmpresaPreferencial() {
      if (this.empresaPreferencial) return this.empresaPreferencial
      const { data } = await this.$http.get('/api/protected/conferenciaFinanceira/empresa-preferencial')
      this.empresaPreferencial = data || {}
      return this.empresaPreferencial
    },
    montarGraficoPizzaHtml(titulo, slices, textoCentro = '') {
      const total = slices.reduce((acc, item) => acc + Number(item.value || 0), 0)
      let acumulado = 0
      const gradiente = total > 0
        ? slices.map(item => {
          const percentual = Number(item.value || 0) / total * 100
          const inicio = acumulado
          const fim = acumulado + percentual
          acumulado = fim
          return `${item.color} ${inicio.toFixed(2)}% ${fim.toFixed(2)}%`
        }).join(', ')
        : '#dfe6eb 0% 100%'

      const legenda = slices.map(item => `
        <div class="pie-legend-item">
          <span class="pie-dot" style="background:${item.color};"></span>
          <span>${escapeHtml(item.label)}</span>
          <strong>${escapeHtml(item.formatted)}</strong>
        </div>
      `).join('')

      return `
        <div class="pie-card">
          <div class="pie-title">${escapeHtml(titulo)}</div>
          <div class="pie-row">
            <div class="pie-chart" style="background: conic-gradient(${gradiente});">
              <div class="pie-center">${escapeHtml(textoCentro)}</div>
            </div>
            <div class="pie-legend">${legenda}</div>
          </div>
        </div>
      `
    },
    montarHtmlEspelhoProcesso(empresa) {
      const nomeUsuario = window.localStorage.getItem('nomeusuario') || window.localStorage.getItem('login') || 'sistema'
      const quantidadePrevista = Number(this.registro.QUANT_PREVISTA || 0)
      const quantidadeProduzida = Number(this.registro.QUANT_PRODUZIDA || 0)
      const quantidadeSobra = Number(this.registro.QUANT_SOBRA || 0)
      const quantidadePerda = Number(this.registro.QUANT_PERDA || 0)
      const saldoPlanejado = Math.max(0, quantidadePrevista - quantidadeProduzida - quantidadeSobra - quantidadePerda)
      const itensComCentro = this.itensExibicao.filter(item => this.itemPossuiCentroSelecionado(item)).length
      const itensPendentes = Math.max(0, this.itensExibicao.length - itensComCentro)

      const graficoAndamento = this.montarGraficoPizzaHtml('Resultado do Processo', [
        { label: 'Produzido', value: quantidadeProduzida, formatted: this.formatarNumero(quantidadeProduzida), color: '#2eb85c' },
        { label: 'Sobra', value: quantidadeSobra, formatted: this.formatarNumero(quantidadeSobra), color: '#f9b115' },
        { label: 'Perda', value: quantidadePerda, formatted: this.formatarNumero(quantidadePerda), color: '#e55353' },
        { label: 'Saldo planejado', value: saldoPlanejado, formatted: this.formatarNumero(saldoPlanejado), color: '#6c757d' }
      ], this.formatarNumero(quantidadePrevista))

      const graficoLotes = this.montarGraficoPizzaHtml('Selecao dos Itens', [
        { label: 'Centro definido', value: itensComCentro, formatted: this.formatarInteiro(itensComCentro), color: '#339af0' },
        { label: 'Centro pendente', value: itensPendentes, formatted: this.formatarInteiro(itensPendentes), color: '#adb5bd' }
      ], this.formatarInteiro(this.itensExibicao.length))

      const linhasItens = this.itensExibicao.map((item, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(item.PRODUTO_CODIGONOME || '')}</td>
          <td>${escapeHtml(item.CENTRO_ESTOQUE_CODIGONOME || '')}</td>
          <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_PREVISTA || 0))}</td>
          <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.CUSTO_UNIT_PREV || 0))}</td>
          <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.CUSTO_TOT_PREV || 0))}</td>
          <td style="text-align:center;">${escapeHtml(item.CENTRO_ESTOQUE_ID ? 'Sim' : 'Nao')}</td>
          <td style="text-align:right;">${escapeHtml(this.formatarNumero(item.QUANT_DISPONIVEL || 0))}</td>
        </tr>
      `).join('')

      return `<!doctype html>
      <html lang="pt-BR">
      <head>
        <meta charset="utf-8">
        <title>Espelho do Processo de Produção por Fórmula</title>
        <style>
          body { font-family: Arial, Helvetica, sans-serif; margin: 16px; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .report-header { border: 1px solid #2f2f2f; }
          .report-header-top, .report-header-sub { display: flex; justify-content: space-between; padding: 4px 8px; font-size: 11px; border-bottom: 1px solid #2f2f2f; }
          .report-header-title { padding: 4px 8px; font-size: 12px; font-weight: 700; text-transform: uppercase; }
          .section { margin-top: 10px; page-break-inside: avoid; }
          .section-title { font-size: 11px; font-weight: 700; margin-bottom: 4px; text-transform: uppercase; }
          .grid, .info-table { width: 100%; border-collapse: collapse; }
          .grid th, .grid td, .info-table td { border: 1px solid #2f2f2f; padding: 4px 6px; font-size: 11px; vertical-align: top; }
          .grid th { background: #f2f2f2; text-align: left; }
          .info-table td { width: 50%; }
          .charts-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-top: 10px; }
          .pie-card { border: 1px solid #2f2f2f; padding: 10px; min-height: 220px; }
          .pie-title { font-size: 11px; font-weight: 700; text-transform: uppercase; margin-bottom: 8px; text-align: center; }
          .pie-row { display: flex; flex-direction: column; align-items: center; gap: 14px; }
          .pie-chart { width: 110px; height: 110px; border-radius: 50%; position: relative; flex-shrink: 0; }
          .pie-center { position: absolute; inset: 22px; border-radius: 50%; background: #fff; display: flex; align-items: center; justify-content: center; text-align: center; font-size: 14px; font-weight: 700; }
          .pie-legend { width: 100%; }
          .pie-legend-item { display: flex; justify-content: space-between; align-items: center; font-size: 10px; margin-bottom: 6px; border-bottom: 1px dashed #eee; padding-bottom: 2px; }
          .pie-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; margin-right: 6px; flex-shrink: 0; }
          .signature-area { margin-top: 40px; }
          .signature-line { border-top: 1px solid #2f2f2f; width: 320px; padding-top: 6px; font-size: 11px; text-align: center; }
          .report-footer { position: fixed; left: 16px; right: 16px; bottom: 8px; display: flex; justify-content: space-between; font-size: 10px; border-top: 1px solid #2f2f2f; padding-top: 4px; background: #fff; }
          @media print { @page { size: A4 portrait; margin: 10mm; } body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div class="report-header-top">
            <div><strong>${escapeHtml(empresa.FANTASIA || empresa.RAZAO_RELATORIO || empresa.RAZAO || 'CECWEB')}</strong></div>
            <div><strong>Usuário:</strong> ${escapeHtml(nomeUsuario)}</div>
          </div>
          <div class="report-header-title">Espelho do Processo de Produção por Fórmula</div>
          <div class="report-header-sub">
            <div>${escapeHtml(empresa.NOME_EMPRESA || '')}</div>
            <div><strong>Emitido em:</strong> ${escapeHtml(formatarDataHoraRelatorio())}</div>
          </div>
          <div class="report-header-sub" style="border-bottom:0;">
            <div><strong>Produto Final:</strong> ${escapeHtml(this.registro.PRODUTO_CODIGONOME || '')}</div>
            <div><strong>Situação:</strong> ${escapeHtml(this.getLabelStatus(this.registro.SITUACAO))}</div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Dados do processo</div>
          <table class="info-table">
            <tr>
              <td><strong>ID:</strong> ${escapeHtml(this.registro.ID || 'Novo')}</td>
              <td><strong>Fórmula:</strong> ${escapeHtml(this.registro.FORMULA_DESCRICAO || '')}</td>
            </tr>
            <tr>
              <td><strong>Lote Final:</strong> ${escapeHtml(this.registro.LOTE || '')}</td>
              <td><strong>Centro de Consumo:</strong> ${escapeHtml(this.registro.CENTRO_CONSUMO_CODIGONOME || this.registro.CENTRO_CONSUMO_ID || '')}</td>
            </tr>
            <tr>
              <td><strong>Centro de Produção:</strong> ${escapeHtml(this.registro.CENTRO_PRODUCAO_CODIGONOME || this.registro.CENTRO_PRODUCAO_ID || '')}</td>
              <td><strong>Total de Apontamentos Ativos:</strong> ${escapeHtml(this.formatarInteiro(this.registro.TOTAL_APONTAMENTOS_ATIVOS || 0))}</td>
            </tr>
            <tr>
              <td><strong>Produção Prevista:</strong> ${escapeHtml(this.formatarNumero(this.registro.QUANT_PREVISTA || 0))}</td>
              <td><strong>Peças/Volumes Previstas:</strong> ${escapeHtml(this.formatarInteiro(this.registro.PECAS_PREVISTA || 0))}</td>
            </tr>
            <tr>
              <td><strong>Produção Acumulada:</strong> ${escapeHtml(this.formatarNumero(this.registro.QUANT_PRODUZIDA || 0))}</td>
              <td><strong>Sobra Acumulada:</strong> ${escapeHtml(this.formatarNumero(this.registro.QUANT_SOBRA || 0))}</td>
            </tr>
            <tr>
              <td><strong>Perda Acumulada:</strong> ${escapeHtml(this.formatarNumero(this.registro.QUANT_PERDA || 0))}</td>
              <td><strong>Custo Total Previsto:</strong> ${escapeHtml(this.formatarMoeda(this.totalCustoPrevisto || 0))}</td>
            </tr>
            <tr>
              <td colspan="2"><strong>Observação:</strong> ${escapeHtml(this.registro.OBSERVACAO || '')}</td>
            </tr>
          </table>
        </div>

        <div class="section">
          <div class="section-title">Indicadores visuais</div>
          <div class="charts-grid">
            ${graficoAndamento}
            ${graficoLotes}
          </div>
        </div>

        <div class="section">
          <div class="section-title">Itens previstos do processo</div>
          <table class="grid">
            <thead>
              <tr>
                <th style="width:36px;">#</th>
                <th style="width:220px;">Matéria-Prima</th>
                <th style="width:180px;">Centro</th>
                <th style="width:100px; text-align:right;">Consumo Prev.</th>
                <th style="width:90px; text-align:right;">Custo Unit.</th>
                <th style="width:100px; text-align:right;">Custo Total</th>
                <th style="width:70px; text-align:center;">Selec.</th>
                <th style="width:90px; text-align:right;">Saldo Líq.</th>
              </tr>
            </thead>
            <tbody>
              ${linhasItens}
            </tbody>
          </table>
        </div>

        <div class="signature-area">
          <div class="signature-line">Assinatura do usuário responsável</div>
        </div>

        <div class="report-footer">
          <div>Impresso em: ${escapeHtml(formatarDataHoraRelatorio())}</div>
          <div>Qtd. itens: ${escapeHtml(this.formatarInteiro(this.totalItensPrevistos || 0))}</div>
          <div>Usuário: ${escapeHtml(nomeUsuario)}</div>
        </div>
      </body>
      </html>`
    },
    async imprimirEspelhoProcesso() {
      if (!this.itensExibicao.length) {
        this.$bvToast.toast('Carregue ou gere os itens do processo antes de gerar o espelho.', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      const janela = window.open('', '_blank')
      if (!janela) {
        this.$bvToast.toast('O navegador bloqueou a abertura da nova aba do espelho.', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      try {
        const empresa = await this.obterEmpresaPreferencial()
        const html = this.montarHtmlEspelhoProcesso(empresa)
        janela.document.open('text/html', 'replace')
        janela.document.write(html)
        janela.document.close()
        janela.focus()
      } catch (err) {
        janela.close()
        this.$bvToast.toast(err?.response?.data || err?.message || 'Erro ao gerar espelho do processo', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      }
    },
    voltar() {
      this.$router.push('/producao/formula-processo/listagem')
    },
    normalizarItemExibicao(itemFormula, indice, itemAtual = {}, manterQuantidadesAtuais = false) {
      const fator = this.calcularFator()
      const quantidadeBase = Number(itemFormula?.QUANTIDADE || itemAtual.QUANTIDADE || 0)
      const pecasBase = Number(itemFormula?.PECAS || itemAtual.PECAS || 0)
      const custoUnit = Number(itemFormula?.VL_CUSTO || itemAtual.CUSTO_UNIT_PREV || 0)
      const quantidadePrevista = manterQuantidadesAtuais
        ? Number(itemAtual.QUANT_PREVISTA || 0)
        : (quantidadeBase > 0 ? quantidadeBase * fator : 0)
      const pecasPrevistas = manterQuantidadesAtuais
        ? Number(itemAtual.PECAS_PREVISTA || 0)
        : (pecasBase > 0 ? Math.round(pecasBase * fator) : 0)
      const centroSugeridoId = itemAtual.CENTRO_ESTOQUE_ID_SUGERIDO || itemFormula?.CENTRO_ESTOQUE_ID_SUGERIDO || itemFormula?.CENTRO_ESTOQUE_ID || this.registro.CENTRO_CONSUMO_ID || null
      const centroSugeridoCodigonome = itemAtual.CENTRO_ESTOQUE_CODIGONOME_SUGERIDO || itemFormula?.CENTRO_ESTOQUE_CODIGONOME_SUGERIDO || itemFormula?.CENTRO_ESTOQUE_CODIGONOME || (centroSugeridoId ? `Centro ${centroSugeridoId}` : '')
      const centroEscolhidoId = itemAtual.CENTRO_ESTOQUE_ID || null
      const centroEscolhidoCodigonome = itemAtual.CENTRO_ESTOQUE_CODIGONOME || ''
      const opCentroSelecionadoManual = String(itemAtual.OP_CENTRO_SELECIONADO_MANUAL || (centroEscolhidoId ? '1' : '0')) === '1' ? '1' : '0'
      const disponibilidadeConsultada = !!centroEscolhidoId && (
        itemAtual.DISPONIBILIDADE_CONSULTADA || itemAtual.ESTOQUE_QUANT !== undefined || itemAtual.ESTOQUE_PECAS !== undefined || itemAtual.QUANT_DISPONIVEL !== undefined || itemAtual.PECAS_DISPONIVEL !== undefined
      )

      return {
        ...itemFormula,
        ...itemAtual,
        FORMULA_ITEM_ID: Number(itemFormula?.ID || itemAtual.FORMULA_ITEM_ID || null),
        ITEM_ORDEM: indice,
        ID: itemAtual.ID || null,
        PRODUTO_ID: Number(itemFormula?.PRODUTO_ID || itemAtual.PRODUTO_ID || null),
        PRODUTO_CODIGONOME: itemAtual.PRODUTO_CODIGONOME || itemFormula?.PRODUTO_CODIGONOME || '',
        CENTRO_ESTOQUE_ID_SUGERIDO: centroSugeridoId,
        CENTRO_ESTOQUE_CODIGONOME_SUGERIDO: centroSugeridoCodigonome,
        CENTRO_ESTOQUE_ID: centroEscolhidoId,
        CENTRO_ESTOQUE_CODIGONOME: centroEscolhidoCodigonome,
        OP_CENTRO_SELECIONADO_MANUAL: opCentroSelecionadoManual,
        QUANT_PREVISTA: quantidadePrevista,
        PECAS_PREVISTA: pecasPrevistas,
        CUSTO_UNIT_PREV: custoUnit,
        CUSTO_TOT_PREV: quantidadePrevista * custoUnit,
        OP_CONTROLA_LOTE: '0',
        ESTOQUE_QUANT: Number(itemAtual.ESTOQUE_QUANT || 0),
        ESTOQUE_PECAS: Number(itemAtual.ESTOQUE_PECAS || 0),
        QUANT_RESERVADA: Number(itemAtual.QUANT_RESERVADA || 0),
        PECAS_RESERVADA: Number(itemAtual.PECAS_RESERVADA || 0),
        QUANT_DISPONIVEL: Number(itemAtual.QUANT_DISPONIVEL || 0),
        PECAS_DISPONIVEL: Number(itemAtual.PECAS_DISPONIVEL || 0),
        DISPONIBILIDADE_CONSULTADA: !!disponibilidadeConsultada,
        OBSERVACAO: itemAtual.OBSERVACAO || itemFormula?.OBSERVACAO || ''
      }
    },
    montarItensPersistidos(itensPersistidos) {
      return (itensPersistidos || []).map((item, indice) => this.normalizarItemExibicao(this.itensFormulaBase[indice] || item, indice, item, true))
    },
    atualizarPreviewItens() {
      if (!this.formulaAtual || !this.itensFormulaBase.length) {
        this.itensPreview = []
        return
      }

      const itensAtuais = this.itensPreview.length ? this.itensPreview : this.itensStore
      this.itensPreview = this.itensFormulaBase.map((item, indice) => this.normalizarItemExibicao(item, indice, itensAtuais[indice] || {}, false))
    },
    calcularFator() {
      return Number(this.registro.MULTIPLICADOR || 1)
    },
    async inicializarNovoRegistro() {
      this.registro = novoRegistro()
      this.registro.DATA_PROCESSO = Tools.getDataAtual()
      this.itensPreview = []
      try {
        const sugestao = await this.$store.dispatch('producaoFormulaProcesso/GET_LOTE_SUGERIDO')
        if (sugestao) {
          this.registro.ID = sugestao.ID
          this.registro.LOTE = sugestao.LOTE
          this.registro.DATA_PROCESSO = sugestao.DATA_PROCESSO || this.registro.DATA_PROCESSO
          this.registro.SITUACAO = sugestao.SITUACAO || 'L'
          this.registro.SITUACAO_LABEL = sugestao.SITUACAO_LABEL || 'Lançamento'
        }
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Não foi possível obter o lote sugerido', {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
      }
    },
    async carregarRegistro(id) {
      this.isBusy = true
      try {
        const processo = await this.$store.dispatch('producaoFormulaProcesso/GET_BY_ID', id)
        this.registro = { ...novoRegistro(), ...processo }
        this.registro.DATA_PROCESSO = Tools.formatarDataParaInput(this.registro.DATA_PROCESSO || this.registro.DATA_CADASTRO)
        const itens = await this.$store.dispatch('producaoFormulaProcesso/GET_ITENS', id)
        if (this.registro.PRD_FORMULA_ID) {
          await this.carregarFormulaBase(this.registro.PRD_FORMULA_ID, false, false)
          this.itensPreview = this.montarItensPersistidos(itens)
          
          // Reconstitui o multiplicador com base no registro carregado
          const pecasPadrao = Number(this.formulaAtual?.PECAS_PADRAO || 0)
          const quantPadrao = Number(this.formulaAtual?.QUANT_PADRAO || 0)
          
          if (pecasPadrao > 0) {
            this.registro.MULTIPLICADOR = Number(this.registro.PECAS_PREVISTA || 0) / pecasPadrao
          } else if (quantPadrao > 0) {
            this.registro.MULTIPLICADOR = Number(this.registro.QUANT_PREVISTA || 0) / quantPadrao
          } else {
            this.registro.MULTIPLICADOR = 1
          }
        }
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao carregar processo', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
        this.voltar()
      } finally {
        this.isBusy = false
      }
    },
    async onFormulaChange(formulaId) {
      if (!this.podeEditarEstrutura) return
      if (!formulaId) {
        this.formulaAtual = null
        this.itensFormulaBase = []
        this.itensPreview = []
        this.registro.PRODUTO_ID = null
        this.registro.PRODUTO_CODIGONOME = ''
        this.registro.FORMULA_DESCRICAO = ''
        return
      }
      await this.carregarFormulaBase(formulaId, true, true)
    },
    async carregarFormulaBase(formulaId, redefinirPrevisao, resetarItens = false) {
      const [formulaResponse, itensResponse] = await Promise.all([
        axios.get(`/api/protected/producao-formula/getById/${formulaId}`),
        axios.get(`/api/protected/producao-formula-item/listar/${formulaId}`)
      ])

      this.formulaAtual = formulaResponse.data?.[0] || null
      this.itensFormulaBase = itensResponse.data || []

      if (this.formulaAtual) {
        this.registro.PRD_FORMULA_ID = this.formulaAtual.ID
        this.registro.FORMULA_DESCRICAO = this.formulaAtual.DESCRICAO
        this.registro.PRODUTO_ID = this.formulaAtual.PRODUTO_ID
        this.registro.PRODUTO_CODIGONOME = this.formulaAtual.PRODUTO_CODIGONOME
        this.registro.ID_UNIDADE_COMERCIAL = this.formulaAtual.ID_UNIDADE_COMERCIAL

        if (redefinirPrevisao || (Number(this.registro.QUANT_PREVISTA || 0) <= 0 && Number(this.registro.PECAS_PREVISTA || 0) <= 0)) {
          this.registro.QUANT_PREVISTA = Number(this.formulaAtual.QUANT_PADRAO || 0)
          this.registro.PECAS_PREVISTA = Number(this.formulaAtual.PECAS_PADRAO || 0)
          this.registro.MULTIPLICADOR = 1
        }

        this.sincronizarCentroConsumoInformativo()
      }

      if (resetarItens) {
        this.itensPreview = []
      }

      this.atualizarPreviewItens()
    },
    sincronizarCentroConsumoInformativo() {
      const sugestoes = this.centrosConsumoSugeridos
      if (!sugestoes.length) return

      const centroAtual = Number(this.registro.CENTRO_CONSUMO_ID || 0)
      const centroEncontrado = sugestoes.find(item => item.id === centroAtual)
      const centroBase = centroEncontrado || sugestoes[0]

      if (!centroAtual) {
        this.registro.CENTRO_CONSUMO_ID = centroBase.id
      }

      this.registro.CENTRO_CONSUMO_CODIGONOME = sugestoes.length === 1
        ? centroBase.label
        : `${sugestoes.length} centros sugeridos na fórmula`
    },
    async regerarPreviewItens() {
      if (!this.podeEditarEstrutura) return

      if (this.itensExibicao.length > 0) {
        const confirmar = await this.$bvModal.msgBoxConfirm(
          'A previsão atual será recalculada com base na fórmula ativa e nos valores informados. Centros já escolhidos serão mantidos apenas quando a mesma linha continuar existindo. Deseja continuar?',
          {
            title: 'Regerar previsão',
            okTitle: 'Sim, regerar',
            cancelTitle: 'Cancelar',
            okVariant: 'primary',
            cancelVariant: 'secondary',
            centered: true
          }
        )

        if (!confirmar) return
      }

      this.atualizarPreviewItens()
    },
    itemControlaLote() {
      return false
    },
    itemPossuiLoteSelecionado() {
      return false
    },
    calcularReservaLocalOutrosItens(itemAtual, indiceAtual) {
      return this.itensExibicao.reduce((acumulado, item, indice) => {
        if (indice === indiceAtual) return acumulado
        if (Number(item.PRODUTO_ID || 0) !== Number(itemAtual.PRODUTO_ID || 0)) return acumulado

        if (Number(item.CENTRO_ESTOQUE_ID || 0) !== Number(itemAtual.CENTRO_ESTOQUE_ID || 0)) {
          return acumulado
        }

        acumulado.quantidade += Number(item.QUANT_PREVISTA || 0)
        acumulado.pecas += Number(item.PECAS_PREVISTA || 0)
        return acumulado
      }, { quantidade: 0, pecas: 0 })
    },
    obterSaldoLinha(item, indice) {
      if (!item || !item.DISPONIBILIDADE_CONSULTADA) return null
      if (!this.itemPossuiCentroSelecionado(item)) return null

      const reservasLocais = this.calcularReservaLocalOutrosItens(item, indice)
      return {
        quantidade: Number(item.QUANT_DISPONIVEL || 0) - reservasLocais.quantidade,
        pecas: Number(item.PECAS_DISPONIVEL || 0) - reservasLocais.pecas
      }
    },
    itemPossuiCentroSelecionado(item) {
      return !!Number(item?.CENTRO_ESTOQUE_ID || 0)
    },
    itemTemSaldoSuficiente(item, indice) {
      const saldo = this.obterSaldoLinha(item, indice)
      if (!saldo) return false
      const atendeQuantidade = Number(item.QUANT_PREVISTA || 0) <= 0 || saldo.quantidade >= (Number(item.QUANT_PREVISTA || 0) - 0.0001)
      const atendePecas = Number(item.PECAS_PREVISTA || 0) <= 0 || saldo.pecas >= Number(item.PECAS_PREVISTA || 0)
      return atendeQuantidade && atendePecas
    },
    itemCentroSelecionadoInsuficiente(item, indice) {
      return this.itemPossuiCentroSelecionado(item) && item.DISPONIBILIDADE_CONSULTADA && !this.itemTemSaldoSuficiente(item, indice)
    },
    itemLoteSelecionadoInsuficiente(item, indice) {
      return false
    },
    normalizarCentroModal(centro, item, indice) {
      const itemReferencia = {
        ...item,
        CENTRO_ESTOQUE_ID: centro.CENTRO_ESTOQUE_ID
      }
      const reservasLocais = this.calcularReservaLocalOutrosItens(itemReferencia, indice)
      const quantReservadaAjustada = Number(centro.QUANT_RESERVADA || 0) + reservasLocais.quantidade
      const pecasReservadaAjustada = Number(centro.PECAS_RESERVADA || 0) + reservasLocais.pecas
      const quantDisponivelAjustado = Number(centro.QUANT_DISPONIVEL || 0) - reservasLocais.quantidade
      const pecasDisponivelAjustado = Number(centro.PECAS_DISPONIVEL || 0) - reservasLocais.pecas
      const saldoSuficiente = (Number(item.QUANT_PREVISTA || 0) <= 0 || quantDisponivelAjustado >= (Number(item.QUANT_PREVISTA || 0) - 0.0001)) &&
        (Number(item.PECAS_PREVISTA || 0) <= 0 || pecasDisponivelAjustado >= Number(item.PECAS_PREVISTA || 0))

      return {
        ...centro,
        QUANT_RESERVADA_AJUSTADA: quantReservadaAjustada,
        PECAS_RESERVADA_AJUSTADA: pecasReservadaAjustada,
        QUANT_DISPONIVEL_AJUSTADO: quantDisponivelAjustado,
        PECAS_DISPONIVEL_AJUSTADO: pecasDisponivelAjustado,
        SALDO_SUFICIENTE: saldoSuficiente,
        IS_SUGERIDO: Number(centro.CENTRO_ESTOQUE_ID || 0) === Number(item.CENTRO_ESTOQUE_ID_SUGERIDO || 0),
        IS_SELECIONADO: Number(centro.CENTRO_ESTOQUE_ID || 0) === Number(item.CENTRO_ESTOQUE_ID || 0)
      }
    },
    normalizarLoteModal(lote, item, indice) {
      const itemReferencia = {
        ...item,
        ESTOQUE_LOTE_ID: lote.ESTOQUE_LOTE_ID
      }
      const reservasLocais = this.calcularReservaLocalOutrosItens(itemReferencia, indice)
      const quantDisponivelAjustado = Number(lote.QUANT_DISPONIVEL || 0) - reservasLocais.quantidade
      const pecasDisponivelAjustado = Number(lote.PECAS_DISPONIVEL || 0) - reservasLocais.pecas
      const saldoSuficiente = (Number(item.QUANT_PREVISTA || 0) <= 0 || quantDisponivelAjustado >= (Number(item.QUANT_PREVISTA || 0) - 0.0001)) &&
        (Number(item.PECAS_PREVISTA || 0) <= 0 || pecasDisponivelAjustado >= Number(item.PECAS_PREVISTA || 0))

      return {
        ...lote,
        QUANT_DISPONIVEL_AJUSTADO: quantDisponivelAjustado,
        PECAS_DISPONIVEL_AJUSTADO: pecasDisponivelAjustado,
        SALDO_SUFICIENTE: saldoSuficiente,
        IS_SELECIONADO: Number(lote.ESTOQUE_LOTE_ID || 0) === Number(item.ESTOQUE_LOTE_ID || 0)
      }
    },
    async abrirModalCentro(item, indice) {
      if (!this.podeEditarEstrutura) return

      this.modalCentro = {
        indice,
        item,
        centros: [],
        carregando: true
      }
      this.$refs['modal-centro-item'].show()

      try {
        const { data } = await this.$http.get(`/api/protected/producao-formula-processo-item/disponibilidade-centros/${item.PRODUTO_ID}`, {
          params: {
            processoId: this.registro.ID || undefined,
            itemId: item.ID || undefined,
            centroSugeridoId: item.CENTRO_ESTOQUE_ID_SUGERIDO || undefined
          }
        })

        this.modalCentro.centros = (data || []).map(centro => this.normalizarCentroModal(centro, item, indice))
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao consultar disponibilidade dos centros', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.modalCentro.carregando = false
      }
    },
    async abrirModalLote(item, indice) {
      if (!this.podeEditarEstrutura) return

      this.modalLote = {
        indice,
        item,
        lotes: [],
        carregando: true
      }
      this.$refs['modal-lote-item'].show()

      try {
        const { data } = await this.$http.get(`/api/protected/producao-formula-processo-item/disponibilidade-lotes/${item.PRODUTO_ID}`, {
          params: {
            processoId: this.registro.ID || undefined,
            itemId: item.ID || undefined,
            estoqueLoteId: item.ESTOQUE_LOTE_ID || undefined
          }
        })

        this.modalLote.lotes = (data || []).map(lote => this.normalizarLoteModal(lote, item, indice))
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao consultar disponibilidade dos lotes', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.modalLote.carregando = false
      }
    },
    selecionarCentroModal(centro) {
      if (!this.podeEditarEstrutura) return
      if (!centro || this.modalCentro.indice === null || this.modalCentro.indice === undefined) return

      const indice = this.modalCentro.indice
      const itemAtual = { ...this.itensExibicao[indice] }
      const itemAtualizado = {
        ...itemAtual,
        CENTRO_ESTOQUE_ID: centro.CENTRO_ESTOQUE_ID,
        CENTRO_ESTOQUE_CODIGONOME: centro.CENTRO_ESTOQUE_CODIGONOME,
        OP_CENTRO_SELECIONADO_MANUAL: '1',
        ESTOQUE_QUANT: Number(centro.ESTOQUE_QUANT || 0),
        ESTOQUE_PECAS: Number(centro.ESTOQUE_PECAS || 0),
        QUANT_RESERVADA: Number(centro.QUANT_RESERVADA || 0),
        PECAS_RESERVADA: Number(centro.PECAS_RESERVADA || 0),
        QUANT_DISPONIVEL: Number(centro.QUANT_DISPONIVEL || 0),
        PECAS_DISPONIVEL: Number(centro.PECAS_DISPONIVEL || 0),
        DISPONIBILIDADE_CONSULTADA: true
      }

      this.$set(this.itensPreview, indice, itemAtualizado)
      this.$bvModal.hide('modal-centro-item')
    },
    selecionarLoteModal(lote) {
      if (!this.podeEditarEstrutura) return
      if (!lote || this.modalLote.indice === null || this.modalLote.indice === undefined) return

      const indice = this.modalLote.indice
      const itemAtual = { ...this.itensExibicao[indice] }
      const itemAtualizado = {
        ...itemAtual,
        ESTOQUE_LOTE_ID: lote.ESTOQUE_LOTE_ID,
        LOTE: lote.LOTE || '',
        LOTE_FORNECEDOR: lote.LOTE_FORNECEDOR || '',
        DATA_VALIDADE: lote.DATA_VALIDADE || null,
        OP_ORIGEM: lote.OP_ORIGEM || '',
        CENTRO_ESTOQUE_ID: lote.CENTRO_ESTOQUE_ID,
        CENTRO_ESTOQUE_CODIGONOME: lote.CENTRO_ESTOQUE_CODIGONOME || '',
        OP_CENTRO_SELECIONADO_MANUAL: '0',
        CUSTO_UNIT_PREV: Number(lote.VL_CUSTO_UNITARIO || 0),
        CUSTO_TOT_PREV: Number(itemAtual.QUANT_PREVISTA || 0) * Number(lote.VL_CUSTO_UNITARIO || 0),
        QUANT_SALDO_LOTE: Number(lote.QUANT_SALDO || 0),
        PECAS_SALDO_LOTE: Number(lote.PECAS_SALDO || 0),
        QUANT_RESERVADA_LOTE: Number(lote.QUANT_RESERVADA || 0),
        PECAS_RESERVADA_LOTE: Number(lote.PECAS_RESERVADA || 0),
        QUANT_DISPONIVEL_LOTE: Number(lote.QUANT_DISPONIVEL || 0),
        PECAS_DISPONIVEL_LOTE: Number(lote.PECAS_DISPONIVEL || 0),
        ESTOQUE_QUANT: Number(lote.QUANT_SALDO || 0),
        ESTOQUE_PECAS: Number(lote.PECAS_SALDO || 0),
        QUANT_RESERVADA: Number(lote.QUANT_RESERVADA || 0),
        PECAS_RESERVADA: Number(lote.PECAS_RESERVADA || 0),
        QUANT_DISPONIVEL: Number(lote.QUANT_DISPONIVEL || 0),
        PECAS_DISPONIVEL: Number(lote.PECAS_DISPONIVEL || 0),
        DISPONIBILIDADE_CONSULTADA: true
      }

      this.$set(this.itensPreview, indice, itemAtualizado)
      this.$bvModal.hide('modal-lote-item')
    },
    limparCentroItem(indice) {
      if (!this.podeEditarEstrutura) return
      const itemAtual = { ...this.itensExibicao[indice] }
      const itemAtualizado = {
        ...itemAtual,
        CENTRO_ESTOQUE_ID: null,
        CENTRO_ESTOQUE_CODIGONOME: '',
        OP_CENTRO_SELECIONADO_MANUAL: '0',
        ESTOQUE_QUANT: 0,
        ESTOQUE_PECAS: 0,
        QUANT_RESERVADA: 0,
        PECAS_RESERVADA: 0,
        QUANT_DISPONIVEL: 0,
        PECAS_DISPONIVEL: 0,
        DISPONIBILIDADE_CONSULTADA: false
      }

      this.$set(this.itensPreview, indice, itemAtualizado)
    },
    limparLoteItem(indice) {
      if (!this.podeEditarEstrutura) return
      const itemAtual = { ...this.itensExibicao[indice] }
      const itemAtualizado = {
        ...itemAtual,
        ESTOQUE_LOTE_ID: null,
        LOTE: '',
        LOTE_FORNECEDOR: '',
        DATA_VALIDADE: null,
        OP_ORIGEM: '',
        CENTRO_ESTOQUE_ID: null,
        CENTRO_ESTOQUE_CODIGONOME: '',
        OP_CENTRO_SELECIONADO_MANUAL: '0',
        CUSTO_UNIT_PREV: 0,
        CUSTO_TOT_PREV: 0,
        QUANT_SALDO_LOTE: 0,
        PECAS_SALDO_LOTE: 0,
        QUANT_RESERVADA_LOTE: 0,
        PECAS_RESERVADA_LOTE: 0,
        QUANT_DISPONIVEL_LOTE: 0,
        PECAS_DISPONIVEL_LOTE: 0,
        ESTOQUE_QUANT: 0,
        ESTOQUE_PECAS: 0,
        QUANT_RESERVADA: 0,
        PECAS_RESERVADA: 0,
        QUANT_DISPONIVEL: 0,
        PECAS_DISPONIVEL: 0,
        DISPONIBILIDADE_CONSULTADA: false
      }

      this.$set(this.itensPreview, indice, itemAtualizado)
    },
    getClasseLinhaModalCentro(item) {
      if (!item) return ''
      if (item.IS_SELECIONADO && item.SALDO_SUFICIENTE) return 'table-primary'
      if (item.IS_SELECIONADO && !item.SALDO_SUFICIENTE) return 'table-danger'
      if (item.IS_SUGERIDO && item.SALDO_SUFICIENTE) return 'table-info'
      if (item.IS_SUGERIDO && !item.SALDO_SUFICIENTE) return 'table-warning'
      if (!item.SALDO_SUFICIENTE) return 'table-danger'
      return ''
    },
    getClasseLinhaModalLote(item) {
      if (!item) return ''
      if (item.IS_SELECIONADO && item.SALDO_SUFICIENTE) return 'table-success'
      if (item.IS_SELECIONADO && !item.SALDO_SUFICIENTE) return 'table-danger'
      if (!item.SALDO_SUFICIENTE) return 'table-warning'
      return ''
    },
    montarPayloadItens() {
      return this.itensExibicao.map(item => ({
        ID: item.ID || null,
        FORMULA_ITEM_ID: item.FORMULA_ITEM_ID || null,
        ITEM_ORDEM: item.ITEM_ORDEM,
        PRODUTO_ID: item.PRODUTO_ID,
        PRODUTO_CODIGONOME: item.PRODUTO_CODIGONOME,
        CENTRO_ESTOQUE_ID: item.CENTRO_ESTOQUE_ID,
        CENTRO_ESTOQUE_CODIGONOME: item.CENTRO_ESTOQUE_CODIGONOME,
        CENTRO_ESTOQUE_ID_SUGERIDO: item.CENTRO_ESTOQUE_ID_SUGERIDO,
        CENTRO_ESTOQUE_CODIGONOME_SUGERIDO: item.CENTRO_ESTOQUE_CODIGONOME_SUGERIDO,
        OP_CENTRO_SELECIONADO_MANUAL: item.OP_CENTRO_SELECIONADO_MANUAL,
        CUSTO_UNIT_PREV: item.CUSTO_UNIT_PREV,
        OBSERVACAO: item.OBSERVACAO || ''
      }))
    },
    validarRegistro() {
      if (!this.registro.DATA_PROCESSO || !Tools.isDataValida(this.registro.DATA_PROCESSO)) return 'Informe uma data válida para o processo'
      if (!this.registro.PRD_FORMULA_ID) return 'Informe a fórmula do processo'
      if (!this.registro.CENTRO_PRODUCAO_ID) return 'Informe o centro de estoque de produção'
      if (!this.registro.LOTE || !String(this.registro.LOTE).trim()) return 'Informe o lote final do processo'
      if (Number(this.registro.MULTIPLICADOR || 0) <= 0) return 'Informe o multiplicador de produção (vezes/batidas)'
      if (this.itensExibicao.length <= 0) return 'É necessário gerar ao menos 1 item previsto antes de salvar o processo'

      for (let indice = 0; indice < this.itensExibicao.length; indice += 1) {
        const item = this.itensExibicao[indice]
        if (!item.CENTRO_ESTOQUE_ID) {
          return `Existe pendência de centro no item ${item.PRODUTO_CODIGONOME}`
        }
        if (!item.DISPONIBILIDADE_CONSULTADA) {
          return `Consulte e selecione o centro do item ${item.PRODUTO_CODIGONOME}`
        }
        if (!this.itemTemSaldoSuficiente(item, indice)) {
          return `Saldo líquido insuficiente para o item ${item.PRODUTO_CODIGONOME} no centro ${item.CENTRO_ESTOQUE_CODIGONOME}`
        }
      }

      return ''
    },
    async gravar() {
      if (!this.podeEditarCabecalho) return

      const erro = this.validarRegistro()
      if (erro) {
        this.$bvToast.toast(erro, {
          title: 'Aviso',
          variant: 'warning',
          solid: true
        })
        return
      }

      this.isBusy = true
      try {
        const url = this.$route.params.id
          ? '/api/protected/producao-formula-processo/edit'
          : '/api/protected/producao-formula-processo/post'
        const { data } = await this.$http.post(url, {
          login: window.localStorage.getItem('login') || 'SISTEMA',
          registro: this.registro,
          itens: this.montarPayloadItens()
        })

        const processoSalvo = data[0]
        this.registro = { ...novoRegistro(), ...processoSalvo }
        this.$store.commit('producaoFormulaProcesso/SET_PROCESSO_ATUAL', this.registro)
        this.$store.commit('producaoFormulaProcesso/SET_RECARREGAR_LISTA', true)

        if (!this.$route.params.id) {
          await this.$router.replace(`/producao/formula-processo/edit/${this.registro.ID}`)
        }

        const itensAtualizados = await this.$store.dispatch('producaoFormulaProcesso/GET_ITENS', this.registro.ID)
        if (this.registro.PRD_FORMULA_ID) {
          await this.carregarFormulaBase(this.registro.PRD_FORMULA_ID, false, false)
          this.itensPreview = this.montarItensPersistidos(itensAtualizados)
        }

        this.$bvToast.toast('Processo gravado com sucesso', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao gravar processo', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isBusy = false
      }
    }
    ,async finalizarProcesso() {
      if (!this.podeFinalizarProcesso) return

      const confirmar = await this.$bvModal.msgBoxConfirm(
        'Após finalizar, o processo ficará somente leitura e não permitirá novos apontamentos nem cancelamento de apontamentos. Deseja continuar?',
        {
          title: 'Finalizar processo',
          okTitle: 'Finalizar',
          cancelTitle: 'Voltar',
          okVariant: 'warning',
          cancelVariant: 'secondary',
          centered: true
        }
      )

      if (!confirmar) return

      this.isBusy = true
      try {
        const { data } = await this.$http.post('/api/protected/producao-formula-processo/finalizar', {
          id: this.registro.ID,
          login: window.localStorage.getItem('login') || 'SISTEMA'
        })

        this.registro = { ...novoRegistro(), ...(data[0] || {}) }
        this.$store.commit('producaoFormulaProcesso/SET_PROCESSO_ATUAL', this.registro)
        this.$store.commit('producaoFormulaProcesso/SET_RECARREGAR_LISTA', true)

        this.$bvToast.toast('Processo finalizado com sucesso', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao finalizar processo', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isBusy = false
      }
    },
    async reabrirProcesso() {
      if (!this.podeReabrirProcesso) return

      const confirmar = await this.$bvModal.msgBoxConfirm(
        'A reabertura irá apenas desfazer a finalização e voltar o processo para lançamento. Deseja continuar?',
        {
          title: 'Reabrir processo',
          okTitle: 'Reabrir',
          cancelTitle: 'Voltar',
          okVariant: 'info',
          cancelVariant: 'secondary',
          centered: true
        }
      )

      if (!confirmar) return

      this.isBusy = true
      try {
        const { data } = await this.$http.post('/api/protected/producao-formula-processo/reabrir', {
          id: this.registro.ID,
          login: window.localStorage.getItem('login') || 'SISTEMA'
        })

        this.registro = { ...novoRegistro(), ...(data[0] || {}) }
        this.registro.DATA_PROCESSO = Tools.formatarDataParaInput(this.registro.DATA_PROCESSO || this.registro.DATA_CADASTRO)
        this.$store.commit('producaoFormulaProcesso/SET_PROCESSO_ATUAL', this.registro)
        this.$store.commit('producaoFormulaProcesso/SET_RECARREGAR_LISTA', true)

        this.$bvToast.toast('Processo reaberto com sucesso', {
          title: 'Sucesso',
          variant: 'success',
          solid: true
        })
      } catch (err) {
        this.$bvToast.toast(err?.response?.data || 'Erro ao reabrir processo', {
          title: 'Erro',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isBusy = false
      }
    }
  },
  async mounted() {
    this.empresaPreferencial = await this.obterEmpresaPreferencial()
    if (this.$route.params.id) {
      await this.carregarRegistro(this.$route.params.id)
    } else {
      await this.inicializarNovoRegistro()
      this.$store.commit('producaoFormulaProcesso/SET_ITENS_PROCESSO', [])
      this.itensPreview = []
    }
  }
}
</script>

<style scoped>
.table-finance tbody td {
  vertical-align: middle !important;
}

.section-icon {
  width: 40px;
  height: 40px;
}

.header-icon-wrapper {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-info-bar {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.modal-content-scroll {
  max-height: calc(100vh - 240px);
  overflow-y: auto;
}

.premium-modal-content {
  border-radius: 18px;
}

.consumo-resultado__valor {
  color: #16324f;
  font-weight: 700;
}

.consumo-resultado__legenda {
  color: #7a8794;
  font-size: 11px;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.saldo-resultado__valor {
  color: #21455e;
  font-weight: 700;
}

.saldo-resultado__valor--critico {
  color: #b44c3b;
}

.saldo-resultado__badge {
  font-size: 10px;
  font-weight: 600;
}
</style>



// WEBPACK FOOTER //
// src/views/producao/producao-formula-processo/ProducaoFormulaProcessoCadastro.vue